import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterLitigationDateType,
  useDeleteMatterLitigationDateType,
  useUpdateMatterLitigationDateType,
} from "@/lib/data/react-query/matters/matter-litigation-date-type";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { MatterLitigationDateTypeSchema } from "@/lib/data/schemas";
import { MATTER_LITIGATION_DATE_TYPE_CATEGORY_OPTIONS } from "@/lib/data/static/matters/litigationStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterLitigationDateType } from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { toLower } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

interface AddOrEditMatterLitigationDateTypeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface ConditionalProps {
  editing?: boolean;
  activeMatterLitigationDateType?: MatterLitigationDateType;
  isReadOnly?: boolean;
}

const formSchema = MatterLitigationDateTypeSchema.pick({
  label: true,
  category: true,
  description: true,
});

type FormValues = z.infer<typeof formSchema>;

const defaultFormValues: FormValues = {
  label: "",
  category: "",
  description: "",
};

const FORM_ID = "matter-litigation-date-type-form";

export const AddOrEditMatterLitigationDateTypeModal = ({
  open,
  setOpen,
  editing,
  activeMatterLitigationDateType,
  isReadOnly,
}: AddOrEditMatterLitigationDateTypeModalProps & ConditionalProps) => {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values:
      editing && activeMatterLitigationDateType
        ? {
            label: activeMatterLitigationDateType.label,
            category: activeMatterLitigationDateType.category,
            description: activeMatterLitigationDateType.description ?? "",
          }
        : defaultFormValues,
    mode: "onSubmit",
  });

  const createMatterLitigationDateType = useCreateMatterLitigationDateType();
  const updateMatterLitigationDateType = useUpdateMatterLitigationDateType();
  const deleteMatterLitigationDateType = useDeleteMatterLitigationDateType();
  const isFormLoading =
    createMatterLitigationDateType.isPending ||
    updateMatterLitigationDateType.isPending ||
    deleteMatterLitigationDateType.isPending;
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const matterLitigationDateTypes = useMemo(
    () => matterSettingsData?.data.litigationDateTypes ?? [],
    [matterSettingsData],
  );

  const onSubmit = async (data: FormValues) => {
    const description = data.description?.trim();
    if (
      matterLitigationDateTypes.some(
        (matterLitigationDateType) =>
          toLower(matterLitigationDateType.label) === toLower(data.label) &&
          matterLitigationDateType.id !== activeMatterLitigationDateType?.id,
      )
    ) {
      form.setError("label", {
        message: "Litigation date type label must be unique",
      });
      return;
    }
    if (editing) {
      if (!activeMatterLitigationDateType) {
        return;
      }
      if (updateMatterLitigationDateType.isPending) {
        return;
      }
      await updateMatterLitigationDateType.mutateAsync({
        id: activeMatterLitigationDateType.id,
        dateModified: activeMatterLitigationDateType.dateModified,
        ...data,
        description: description || undefined,
      });
    } else {
      if (createMatterLitigationDateType.isPending) {
        return;
      }
      await createMatterLitigationDateType.mutateAsync({
        ...data,
        description: description || undefined,
      });
    }
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!activeMatterLitigationDateType) {
      return;
    }
    setIsDeleting(false);
    setOpen(false);
    await deleteMatterLitigationDateType.mutateAsync({
      id: activeMatterLitigationDateType.id,
    });
  };

  useEffect(() => {
    if (!open) {
      form.reset(defaultFormValues);
      setIsDeleting(false);
    }
  }, [open, form]);

  const canDelete =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterLitigationDateType,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDelete ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={
            editing
              ? "Edit litigation date type"
              : "Create litigation date type"
          }
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && (
                  <FormSubmitButton
                    submitFormId={FORM_ID}
                    loading={isFormLoading}
                    disabled={isFormLoading}
                  />
                )}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className={slideoverFieldsetClassName}
              >
                <TextFormField
                  label="Label*"
                  placeholder="Enter litigation date type label"
                  register={form.register("label")}
                />
                <SimpleSelectFormField
                  label="Category*"
                  placeholder="Select category"
                  items={MATTER_LITIGATION_DATE_TYPE_CATEGORY_OPTIONS}
                  register={form.register("category")}
                />
                <div className="col-span-2">
                  <TextAreaFormField
                    label="Description"
                    register={form.register("description")}
                  />
                </div>
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this litigation date type?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
};
